import { renterListingOf } from '../quote/renterListing.js'
import { readinessOf } from './readiness.js'
import { statusOf } from '../property/homeStatus.js'
import { calendarCheck } from './calendarCheck.js'
import { priceListing, averageMonthlyCents } from './listingPrice.js'
import { verdictFor, VERDICT, MID_TERM_MINIMUM_NIGHTS } from '../availability/search.js'
import { moveInDays } from '../availability/openings.js'
import { assignmentsFrom } from '../pricing/strategy.js'
import { PET_POLICY } from '../property/settings.js'
import { stayPathOf, stayKeyOf, OPENINGS_DAYS, OPENINGS_LONGEST_NIGHTS } from './publicHome.js'
import { fromMonthlyCents } from './fromPrice.js'

/**
 * THE PAGE THAT LISTS EVERY HOME — the search page of our own direct-booking site. (Project doc 105.)
 *
 * The Product Owner, 2026-10-06/07: one page, copied from sites that already do this well (Airbnb's
 * monthly stays, Blueground, Furnished Finder), for renters AND for staff — "维护两个页面很容易…某个
 * 地方更新了，那边却没有更新…做成一个页面就好了". So this file answers both, exactly as
 * `publicHome.js` answers both for one home (#276):
 *
 *   · a renter gets the homes that are ready — the very homes whose own page she could open — and
 *     nothing about the ones that are not;
 *   · signed-in staff get the same cards, plus the homes still being set up, each with what it is
 *     missing (the same `readinessOf` the Listing page used).
 *
 * WHAT A CARD CARRIES IS COPIED OUT BY NAME, as `publicHomeOf` does, so a column added to a home
 * tomorrow is private until somebody decides otherwise. Before she has dates a card carries only
 * "from" — the cheapest month (fromPrice.js, the Product Owner 2026-10-08); her own price is asked
 * for separately once she has chosen a move-in and a move-out, because a home's rent differs by month.
 */

/* ── the window the page reads ─────────────────────────────────────────────────────────────── */

const DAY_MS = 86_400_000
const addDays = (iso, n) => new Date(Date.parse(`${iso}T00:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10)
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/

/**
 * WHAT THE SEARCH PAGE READS, AND WHEN — ONLY WHAT THE QUESTION NEEDS (the Product Owner, 2026-10-07
 * 15:05: "14秒有点久…有需要现在就去读取所有的calendar吗？").
 *
 * The cards need the stretch a home's readiness is judged over and the first day she could move in:
 * the days the calendar offers for a move-in, plus one minimum stay past the last of them — about
 * seven months. That is all the page reads to open. Two years is the furthest a stay she can choose
 * reaches ("日期我们现在就定两年嘛", 11:31), and those nights are read only when she picks dates that
 * reach them — the way a search site keeps the list fast and asks the calendar about her dates.
 */
const LONGEST_REACH_DAYS = 730

export function browseWindowFor(today) {
  return { from: today, to: addDays(today, OPENINGS_DAYS + MID_TERM_MINIMUM_NIGHTS + 1) }
}

/**
 * The window her dates need: from today (readiness is judged from today) to the day after her
 * move-out, rounded up to the end of that month so stays ending in the same month share one read,
 * and never past two years.
 */
export function priceWindowFor(today, checkOut) {
  const list = browseWindowFor(today)
  /* Not a real day (2027-13-01 passes the pattern): the opening window, and the stay check says why. */
  if (!ISO_DAY.test(String(checkOut ?? '')) || Number.isNaN(Date.parse(`${checkOut}T00:00:00Z`))) return list
  const after = addDays(checkOut, 1)
  const [y, m] = after.split('-').map(Number)
  const monthEnd = new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10)
  const cap = addDays(today, LONGEST_REACH_DAYS)
  const to = [list.to, monthEnd].sort().at(-1)
  return { from: today, to: to > cap ? cap : to }
}

/* ── what a home offers, as the chips ask it ───────────────────────────────────────────────── */

/**
 * THE QUICK FILTERS THAT ARE ABOUT THE HOME ITSELF (decision 2). Each is read from where that fact
 * already lives — the pet policy from Property setup, the rest from Guesty's amenity list — and
 * never guessed: a home whose list does not say "Washer" is not a home with a washer.
 *
 * "Utilities included" is not here. Whether utilities are included is decided per quote today
 * (`utilitiesBasis`), not per home, so there is no fact for a chip to read yet.
 */
export const FEATURE = Object.freeze({
  PETS: 'PETS',
  WASHER_DRYER: 'WASHER_DRYER',
  PARKING: 'PARKING',
  WORKSPACE: 'WORKSPACE',
})

export const FEATURE_LABEL = Object.freeze({
  PETS: 'Pets considered',
  WASHER_DRYER: 'Washer & dryer',
  PARKING: 'Parking',
  WORKSPACE: 'Workspace',
})

const has = (amenities, test) => amenities.some((a) => test(String(a).toLowerCase()))

export function featuresOf(unit, settings) {
  const a = Array.isArray(unit?.amenities) ? unit.amenities : []
  const out = []
  const pets = settings?.pets?.policy
  if (pets === PET_POLICY.WITH_FEE || pets === PET_POLICY.CASE_BY_CASE) out.push(FEATURE.PETS)
  if (has(a, (s) => /^washer\b/.test(s)) && has(a, (s) => /^dryer\b/.test(s))) out.push(FEATURE.WASHER_DRYER)
  /*
    PARKING MEANS THE HOME'S OWN (the Product Owner, 2026-10-07 12:14: "只算自家的"): a space on the
    premises or a garage. Street parking and parking off the premises are not this chip.
  */
  if (has(a, (s) => s === 'garage' || /parking on premises/.test(s))) out.push(FEATURE.PARKING)
  if (has(a, (s) => s.includes('workspace') || s === 'desk')) out.push(FEATURE.WORKSPACE)
  return out
}

/* ── one card ──────────────────────────────────────────────────────────────────────────────── */

/** The first day she could move in for at least a minimum stay, or null when none in the window. */
function firstMoveIn(unit, mirror, today) {
  try {
    const d = moveInDays(unit, mirror, { from: today, days: OPENINGS_DAYS })
    return d.open[0] ?? d.needsOwnerOk[0] ?? null
  } catch { return null }
}

function cardOf({ unit, settings, readiness, mirror, today, backups, preview, strategy = null, policies = [] }) {
  const { area, listing } = renterListingOf(unit)
  const first = listing?.photos?.[0] ?? null
  const card = {
    key: stayKeyOf(unit),
    path: stayPathOf(unit),
    /* A Suites room type is named for the type (2026-10-07), like its own page. */
    name: unit.roomTypeId ? unit.name : listing?.title ?? unit.name ?? null,
    city: unit.city ?? null,
    propertyType: unit.propertyType ?? null,
    bedrooms: unit.bedrooms ?? null,
    bathrooms: unit.bathrooms ?? null,
    accommodates: unit.accommodates ?? null,
    /* The vendor's picture first, our own copy for the moment it fails (#262). */
    photo: first ? { url: first.url, ...(backups[first.url] ? { backupUrl: backups[first.url] } : {}) } : null,
    /* The displaced circle, never the home (approximateArea.js): what the map's pin stands on. */
    area: area ? { lat: area.lat, lng: area.lng } : null,
    availableFrom: firstMoveIn(unit, mirror, today),
    features: featuresOf(unit, settings),
    /* The cheapest month as a one-month stay (fromPrice.js): what a card says before she picks dates. */
    fromMonthlyCents: fromMonthlyCents({ unit, settings, strategy, offered: readiness.offeredCleaning, today, policies }),
  }
  if (preview) card.preview = { unitId: unit.id, listable: readiness.listable, missing: readiness.missing }
  return card
}

function judged({ world, mirror, today }) {
  return (world.units ?? []).map((unit) => {
    const settings = world.settingsByUnit?.[unit.id] ?? null
    const status = statusOf(unit, { statuses: world.homeStatuses ?? {}, settings })
    const readiness = readinessOf(unit, settings, { calendar: calendarCheck(unit, mirror, { from: today }), status })
    return { unit, settings, readiness }
  })
}

/**
 * The page's homes. A renter: the ready ones. Staff: every home on offer for mid-term — ready ones
 * first — each saying whether a renter can see it and, if not, why (what the Listing page showed).
 */
export function answerBrowse({ world, mirror, today, preview = false }) {
  const backups = world.photoBackups ?? {}
  const rows = judged({ world, mirror, today })
    .filter((r) => (preview ? r.readiness.midTerm : r.readiness.listable))
    .sort((a, b) => Number(b.readiness.listable) - Number(a.readiness.listable)
      || String(a.unit.name).localeCompare(String(b.unit.name)))
  const strategies = world.strategies ?? []
  const assignments = assignmentsFrom(strategies)
  const policies = world.depositPolicies ?? []
  const homes = rows.map((r) => cardOf({ ...r, mirror, today, backups, preview, policies,
    strategy: strategies.find((s) => s.id === assignments[r.unit.id]) ?? null }))
  return { statusCode: 200, body: { status: 'ok', today, homes, minimumNights: MID_TERM_MINIMUM_NIGHTS } }
}

/* ── the prices, once she has chosen ───────────────────────────────────────────────────────── */

/**
 * HER STAY, AS THE CALENDAR GAVE IT: a move-in day and a move-out day — the same calendar, and so the
 * same limits, as a home's own page (the Product Owner, 2026-10-07 11:58: "换成同一个 move in move out
 * date calendar…做的跟之前的一模一样"): a move-in from today within the days the page offers, and a
 * stay of at least a minimum stay and at most a year.
 */
export function stayFor({ checkIn, checkOut, today }) {
  const errors = []
  if (!ISO_DAY.test(String(checkIn ?? '')) || !ISO_DAY.test(String(checkOut ?? ''))
    || Number.isNaN(Date.parse(`${checkIn}T00:00:00Z`)) || Number.isNaN(Date.parse(`${checkOut}T00:00:00Z`))) {
    return { errors: ['checkIn and checkOut must be dates (YYYY-MM-DD)'] }
  }
  const nights = Math.round((Date.parse(`${checkOut}T00:00:00Z`) - Date.parse(`${checkIn}T00:00:00Z`)) / DAY_MS)
  if (checkIn < today) errors.push('checkIn is in the past')
  else if (checkIn > addDays(today, OPENINGS_DAYS - 1)) errors.push(`checkIn is more than ${OPENINGS_DAYS} days ahead`)
  if (nights < MID_TERM_MINIMUM_NIGHTS) errors.push(`a stay is at least ${MID_TERM_MINIMUM_NIGHTS} nights`)
  else if (nights > OPENINGS_LONGEST_NIGHTS) errors.push(`a stay is at most ${OPENINGS_LONGEST_NIGHTS} nights`)
  return errors.length ? { errors } : { checkIn, checkOut }
}

/**
 * Each home's rent per month on average over her stay — by the same `priceListing` her home's own
 * page and a quote use, so the card, the page and the quote agree to the cent — and whether the home
 * is free for it. Nothing else leaves: no cleaning, no deposit, no floor, no strategy.
 *
 * `free` says whether she can ask for it: true or false, never "unknown" (see below).
 */
export function answerBrowsePrices({ world, mirror, today, now, checkIn: askedIn, checkOut: askedOut, preview = false }) {
  const stay = stayFor({ checkIn: askedIn, checkOut: askedOut, today })
  if (stay.errors) return { statusCode: 400, body: { status: 'invalid_request', errors: stay.errors } }
  const { checkIn, checkOut } = stay
  const strategies = world.strategies ?? []
  const assignments = assignmentsFrom(strategies)
  const prices = {}
  for (const { unit, settings, readiness } of judged({ world, mirror, today })) {
    if (preview ? !readiness.midTerm : !readiness.listable) continue
    let averageCents = null
    if (readiness.offeredCleaning.length > 0 && settings?.monthlyRent != null) {
      try {
        const strategy = strategies.find((s) => s.id === assignments[unit.id]) ?? null
        const p = priceListing({ unit, settings, strategy, checkIn, checkOut, offered: readiness.offeredCleaning,
          asOf: now, policies: world.depositPolicies ?? [] })
        averageCents = averageMonthlyCents(p.option)
      } catch { averageCents = null }
    }
    /*
      FREE OR NOT — NO THIRD ANSWER ON THIS PAGE (the Product Owner, 2026-10-07 11:31). A renter sends
      an inquiry, not a booking, so a stay the owner must agree to (`NEEDS_OWNER_OK`, behind Guesty's
      advance-booking window) is offered like any other: we tell her after she asks. Anything else
      that is not a clean fit — a night taken, a night we could not read, a rule that refuses it — is
      not something she can have, and is not shown.
    */
    let free = false
    try {
      const v = verdictFor(unit, mirror, { checkIn, checkOut })
      free = v.verdict === VERDICT.FITS || v.verdict === VERDICT.NEEDS_OWNER_OK
    } catch { free = false }
    prices[stayKeyOf(unit)] = { averageMonthlyCents: averageCents, free }
  }
  return { statusCode: 200, body: { status: 'ok', checkIn, checkOut, prices } }
}
